/**
 * 预览全文 / 解锁付款用到的纯函数：大纲 → 目录与预览序列、附加服务映射、重新建单请求体、金额格式。
 */

/** 去掉空白（含全角空格）后比较标题 */
export const normTitle = (title) =>
    String(title || '')
        .replace(/[\s　]+/g, '')
        .toLowerCase();

export const isAbstractZh = (title) => normTitle(title) === '摘要';
export const isAbstractEn = (title) => normTitle(title) === 'abstract';

/** 固定尾章：不参与免费预览，也不能单章解锁 */
const FIXED_TAIL = ['参考文献', '致谢', '附录'];
export const isFixedTail = (title) => FIXED_TAIL.includes(normTitle(title));

/**
 * 大纲 → 目录章节：{ key, title, kind, sections:[{ key, title, subsections:[title] }] }
 * kind：abstract-zh / abstract-en / fixed / body
 */
export const buildChapters = (outlines) =>
    (Array.isArray(outlines) ? outlines : []).map((chapter, index) => {
        const title = chapter?.chapter || `第${index + 1}章`;
        let kind = 'body';
        if (isAbstractZh(title)) kind = 'abstract-zh';
        else if (isAbstractEn(title)) kind = 'abstract-en';
        else if (isFixedTail(title)) kind = 'fixed';
        return {
            key: `ch-${index}`,
            title,
            kind,
            sections: (Array.isArray(chapter?.sections) ? chapter.sections : []).map(
                (section, secIndex) => ({
                    key: `ch-${index}-${secIndex}`,
                    title: section?.chapter || '',
                    subsections: (Array.isArray(section?.subsections) ? section.subsections : [])
                        .map((sub) => sub?.chapter)
                        .filter(Boolean),
                }),
            ),
        };
    });

/**
 * 与后端预览顺序一致的预览单元：摘要 → ABSTRACT → 正文最末级小节（没有小节的章本身就是一个单元，如开题报告的模块）。
 * 只用来在流式输出时预先显示标题，完成事件到达后以后端返回的标题为准。
 */
export const buildPreviewUnits = (chapters) => {
    const units = [];
    const zh = chapters.find((c) => c.kind === 'abstract-zh');
    const en = chapters.find((c) => c.kind === 'abstract-en');
    if (zh) {
        units.push({ chapterTitle: zh.title });
        if (en) units.push({ chapterTitle: en.title });
    }
    chapters
        .filter((c) => c.kind === 'body')
        .forEach((chapter) => {
            if (!chapter.sections.length) {
                units.push({ chapterTitle: chapter.title });
                return;
            }
            chapter.sections.forEach((section) => {
                if (section.subsections.length) {
                    section.subsections.forEach((sub) =>
                        units.push({
                            chapterTitle: chapter.title,
                            sectionTitle: section.title,
                            subsectionTitle: sub,
                        }),
                    );
                } else {
                    units.push({ chapterTitle: chapter.title, sectionTitle: section.title });
                }
            });
        });
    return units;
};

export const sameUnit = (a, b) =>
    !!a &&
    !!b &&
    normTitle(a.chapterTitle) === normTitle(b.chapterTitle) &&
    normTitle(a.sectionTitle) === normTitle(b.sectionTitle) &&
    normTitle(a.subsectionTitle) === normTitle(b.subsectionTitle);

/** 下一次预览大概率是哪一节：序列里第一个还没预览过的单元 */
export const predictNextUnit = (units, doneUnits) =>
    units.find((unit) => !doneUnits.some((done) => sameUnit(done, unit))) || null;

export const unitKind = (unit) => {
    if (!unit) return 'body';
    if (isAbstractZh(unit.chapterTitle)) return 'abstract-zh';
    if (isAbstractEn(unit.chapterTitle)) return 'abstract-en';
    return 'body';
};

/** 后端 previewed / 完成事件 → 预览单元 */
export const toUnit = (item) =>
    item && item.chapterTitle
        ? {
              chapterTitle: item.chapterTitle,
              sectionTitle: item.sectionTitle || null,
              subsectionTitle: item.subsectionTitle || null,
          }
        : null;

// ---------- 附加服务 ----------

/** serviceType → /ai/order/create 的 addons 字段 */
export const ADDON_FIELD = {
    opening_report: 'openingReport',
    task_book: 'taskBook',
    questionnaire: 'questionnaire',
    literature_review: 'literatureReview',
    humanize: 'humanize',
    defense_ppt: 'defensePpt',
};

/** 同类型主单隐藏同名附加服务 */
const HIDDEN_ADDONS = {
    OPENING_REPORT: ['opening_report'],
    LITERATURE_REVIEW: ['literature_review'],
};

export const visibleAddons = (items, queryType) => {
    const hidden = HIDDEN_ADDONS[queryType] || [];
    return (Array.isArray(items) ? items : []).filter(
        (item) => item?.serviceType && !hidden.includes(item.serviceType),
    );
};

/** 报价里的 addonServices → { serviceType: true } */
export const addonsFromQuote = (quote) => {
    const map = {};
    (Array.isArray(quote?.addonServices) ? quote.addonServices : []).forEach((item) => {
        if (item?.serviceType) map[item.serviceType] = true;
    });
    return map;
};

export const addonKey = (selected) =>
    Object.keys(selected || {})
        .filter((key) => selected[key])
        .sort()
        .join(',');

export const toAddonsPayload = (selected) => {
    const addons = {};
    Object.entries(ADDON_FIELD).forEach(([serviceType, field]) => {
        addons[field] = !!selected?.[serviceType];
    });
    return addons;
};

// ---------- 重新建单 ----------

const walkOutline = (nodes, visit) =>
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
        if (!node) return;
        visit(node);
        walkOutline(node.sections, visit);
        walkOutline(node.subsections, visit);
    });

/** 大纲里勾选的图表类型并集（autoLightTypes），一个都没有时为 NORMAL */
export const collectLightTypes = (outlines) => {
    const set = new Set();
    walkOutline(outlines, (node) => {
        String(node.selected_extra || '')
            .split(',')
            .map((s) => s.trim().toUpperCase())
            .filter((s) => s && s !== 'NORMAL')
            .forEach((s) => set.add(s));
    });
    return set.size ? Array.from(set).join(',') : 'NORMAL';
};

const hasLevel3 = (outlines) =>
    (Array.isArray(outlines) ? outlines : []).some((chapter) =>
        (chapter?.sections || []).some((section) => (section?.subsections || []).length > 0),
    );

/**
 * 付款弹窗改附加服务时重新下单的请求体：优先用父组件给的原始请求体；
 * 没给时按 props 和报价重建（此时拿不到 customRef，参考文献由后端按 fromUid 沿用）。
 */
export const buildRecreatePayload = ({
    createPayload,
    title,
    queryType,
    model,
    outlines,
    quote,
    addons,
    fromUid,
}) => {
    if (createPayload) return { ...createPayload, addons, fromUid };
    const list = Array.isArray(outlines) ? outlines : [];
    const wordCount = Number(quote?.totalWords);
    return {
        paperTitle: title || quote?.paperTitle || '',
        queryType: queryType || quote?.queryType || 'PAPER',
        outlines: list,
        autoLightTypes: collectLightTypes(list),
        ...(Number.isFinite(wordCount) && wordCount > 0 && { wordCount }),
        model: model || quote?.model || 'normal',
        enableLevel3: hasLevel3(list),
        recommendEnglishLiterature: Number(quote?.englishLiteratureCount) > 0,
        addons,
        fromUid,
    };
};

// ---------- 金额 / 杂项 ----------

export const formatYuan = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(2) : '--';
};

/** 价格明细里的图表项：[数量字段, 价格字段, 名称] */
export const EXTRA_ROWS = [
    ['figureCount', 'figurePrice', '图'],
    ['tableCount', 'tablePrice', '表'],
    ['formulaCount', 'formulaPrice', '公式'],
    ['codeCount', 'codePrice', '代码'],
    ['dataCount', 'dataPrice', '数据'],
];

export const isMobileUa = () =>
    typeof navigator !== 'undefined' &&
    /android|iphone|ipod|windows phone|mobile/i.test(navigator.userAgent);

export const copyText = async (text, message) => {
    try {
        await navigator.clipboard.writeText(text);
        message?.success('已复制');
    } catch {
        message?.info(`请手动复制：${text}`);
    }
};

/** 沉浸式页面 / 弹窗挂到 .app-container 下，才能取到 antd 的 CSS 变量（主题色） */
export const portalContainer = () =>
    (typeof document !== 'undefined' && document.querySelector('.app-container')) ||
    document.body;
